/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import './style/dialog.scss';

import React, {MouseEventHandler} from 'react';
import classNames from 'classnames';
import ButtonController from "../button/buttonController";
import {ControlContext} from "../util/controlContext";
import {enterPressed, generateUUID, hasHover} from "../util/controlUtil";
import {colorIsWhite, convertColor} from "../util/colorUtil";
import {ControlVariant} from "../util/interfaces";
import {DialogActions, DialogActionShape} from "./dialogController";

interface Props {
    onClose?: (id?: string) => void;
    className: string;
    open?: boolean;
    data?: DataShape;
    id?: string;
}

interface State {
    open: boolean;
    message: string | React.ReactElement;
    title: string;
    actions?: DialogActions;
    transparent: boolean;
    content: React.ReactElement | null;
}

interface DataShape {
    message: string | React.ReactElement;
    title?: string;
    actions?: DialogActions;
    transparent?: boolean;
    persistent?: boolean;
    buttonVariant?: ControlVariant;
}

export default class Dialog extends React.Component<Props, State> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;
    static defaultProps = {
        className: '',
    };
    private buttonVariant: ControlVariant = ControlVariant.OUTLINED;

    onClose = () => {
        const {onClose, id} = this.props;
        this.removeKeyListener();
        this.setState({open: false}, () => {
            if (onClose) onClose(id);
        });
    };

    getDefaultAction = (): DialogActions => ['Oke'];

    state: State = {
        open: false,
        message: '',
        content: null,
        title: 'Bericht van het systeem',
        actions: this.getDefaultAction(),
        transparent: false,
    };

    dialog: HTMLElement | null = null;
    id = generateUUID();
    persistent = false;

    onClickBackground = () => {
        if (!this.persistent && this.dialog !== null && !hasHover(this.dialog))
            this.onClose();
    };

    // =============== NOTE Use function below to open the dialog
    onOpen = (data: DataShape) => {
        const message = data.message, title = data.title === undefined ? 'Bericht van het systeem' : data.title,
            actions = data.actions === undefined ? this.getDefaultAction() : data.actions,
            transparent = data.transparent === undefined ? false : data.transparent;
        this.persistent = data.persistent === undefined ? true : data.persistent;
        if (data.buttonVariant) this.buttonVariant = data.buttonVariant;
        this.setState({message, title, actions, transparent}, () => {
            this.setState({open: true});
        });
        this.addKeyListener();
    };

    onEnterPressed = (evt: KeyboardEvent) => {
        evt.stopPropagation();
        const {actions} = this.state;
        if (actions)
            for (const action of actions) {
                if (typeof (action) === 'string' || action.primary) {
                    if (typeof action === 'string') this.onClose();
                    else if (action.onClick) action.onClick(evt as any as React.MouseEvent);
                    break;
                }
            }
    }

    keyListener = (evt: KeyboardEvent) => {
        if (enterPressed(evt)) {
            this.onEnterPressed(evt);
        }
    };

    addKeyListener = () => {
        document.addEventListener('keydown', this.keyListener);
    };

    removeKeyListener = () => {
        document.removeEventListener('keydown', this.keyListener);
    };

    getColorStyle = (): object => {
        const {baseColor, contrastColor} = this.context,
            color = colorIsWhite(baseColor) ? contrastColor : baseColor;
        return {color: convertColor(color)};
    };

    extendOnClick = (action: DialogActionShape): MouseEventHandler => (evt: any) => {
        if (!action.keepOpen)
            this.onClose();
        action.onClick?.(evt);
    };

    getActions = () => {
        const {actions} = this.state;
        if (actions === undefined || actions === null) return this.generateDefaultAction();
        if (Array.isArray(actions))
            return actions.map(this.renderAction);
        return this.renderAction(actions);
    };

    generateDefaultAction = (): React.ReactNode => (
        <ButtonController label={'Oke'} onClick={this.onClose} type='outlined' containerClass='dialogButton'/>
    );

    renderAction = (action: DialogActionShape | string, index?: number) => {
        const keyProp = index === undefined ? {} : {key: index};
        if (typeof action === 'string') return (
            <ButtonController {...keyProp} label={action} onClick={this.onClose}
                              type={this.buttonVariant} containerClass='dialogButton'/>
        );
        return (
            <ButtonController {...keyProp} label={action.label} onClick={this.extendOnClick(action)}
                              type={this.buttonVariant} containerClass='dialogButton' primary={action.primary}/>
        );
    };

    renderTitle = (): React.ReactNode => {
        const {title} = this.state;
        return (
            <div className='dialogTitleContainer'>
                <label className='dialogTitle' style={this.getColorStyle()}>{title}</label>
            </div>
        )
    };

    renderMessage = (): React.ReactNode => {
        const {message} = this.state;
        return (
            <div className='dialogMessageContainer'>
                {typeof message === 'string' ? <p className='dialogMessage'>{message}</p> : message}
            </div>
        )
    };

    renderActions = (): React.ReactNode => {
        const actions = this.getActions();
        return (
            <div className='dialogActionsContainer'>
                {actions}
            </div>
        )
    };

    componentDidMount = () => {
        const {open, data} = this.props;
        if (open && data) this.onOpen(data);
    };

    componentWillUnmount = () => {
        this.removeKeyListener();
    };

    render = () => {
        const {open, transparent} = this.state, {className} = this.props;
        if (!open && !this.props.open) return null;
        return (
            <div className={classNames('dialogContainer', transparent && 'transparent')}
                 onClick={this.onClickBackground}>
                <div className={classNames('dvr-dialog', className)} ref={ref => {
                    this.dialog = ref
                }}>
                    {this.renderTitle()}
                    {this.renderMessage()}
                    {this.renderActions()}
                </div>
            </div>
            // </WithEvents>
        )
    }
}